import React from "react";
import {
  MobileFilled,
  QrcodeOutlined,
  MailFilled,
  PhoneFilled,
  KeyOutlined,
  ClockCircleOutlined,
  TruckOutlined,
  RocketOutlined,
  DollarOutlined,
  FullscreenOutlined,
  ToolOutlined,
  UserSwitchOutlined,
  EnvironmentOutlined,
  FileExcelOutlined,
  SwapOutlined,
  CloseCircleOutlined,
  RetweetOutlined,
  DashboardOutlined,
} from "@ant-design/icons";
import { BillOfLadingMediaPurpose } from "@/typedefs/bol";
import { LoadStatus } from "@/typedefs/load";
import { NissanVehicleStatus } from "@/typedefs/nissan";
import { SalesforceAccountStatus } from "@/typedefs/salesforce";
import { SecurePassLogType, SecurePassTagStatus } from "@/typedefs/securePass";
import {
  ShipmentIncompleteStatus,
  ShipmentStatus,
  RUCARiskScore,
  HardRouteLocation,
  TrailerType,
  ShipmentLocked,
  AutosledCancelReason,
  TransporterCancelReason,
  ShipmentCancelReason,
  ShipperCancelReason,
  DealerExchange,
  DispatchSource,
} from "@/typedefs/shipment";
import { UserType, RoleType } from "@/typedefs/user";
import { GatePassType, VehicleType, QuoteStatus, Drivability, FuelType, QuoteFeedbackType } from "@/typedefs/vehicle";

export const UserTypeToRoleType = {
  [UserType.Group]: RoleType.Dealer,
  [UserType.Transporter]: RoleType.Transporter,
  [UserType.Dealer]: RoleType.Dealer,
};

export const ProfilePictureStatus = {
  Idle: 0,
  Uploading: 1,
  Success: 2,
};

export const ScanStatuses = {
  NOT_SCANNED: 1,
  SUCCESS: 2,
  FAIL: 3,
  MANUAL_SUCCESS: 4,
  MANUAL_FAIL: 5,
};

export const LoadPaymentStatusToMelioStatusMap = {
  0: "unpaid",
  1: "paid",
  2: "scheduled",
  3: "unpaid",
  4: "unpaid",
};

export const RouteReasonMap = {
  1: "Outside major metro",
  2: "Low traffic area",
  3: "Other",
};

export const VehicleTypeMap = {
  [VehicleType.Car]: "Car",
  [VehicleType.Truck]: "Truck",
  [VehicleType.SUV]: "SUV",
  [VehicleType.Van]: "Van",
  [VehicleType.Other]: "Other",
  [VehicleType.Motorcycle]: "Motorcycle",
  [VehicleType.ATV_UTV]: "ATV/UTV",
  [VehicleType.Coupe]: "Coupe",
  [VehicleType.EV]: "EV",
};

export const IncompleteStatusMap = {
  [ShipmentIncompleteStatus.NOT_APPLICABLE]: "Not Applicable",
  [ShipmentIncompleteStatus.INCIDENT]: "Incident",
  [ShipmentIncompleteStatus.MISINFORMATION]: "Misinformation",
  [ShipmentIncompleteStatus.NOT_AVAILABLE]: "Not Available",
  [ShipmentIncompleteStatus.CANCELLED]: "Cancelled",
  [ShipmentIncompleteStatus.DRY_RUN]: "Dry Run",
};

export const ShipmentStatusMap = {
  [ShipmentStatus.Draft]: "Draft",
  [ShipmentStatus.Pending]: "Pending Quotes",
  [ShipmentStatus.Completed]: "Completed Quotes",
  [ShipmentStatus.Posted]: "Posted",
  [ShipmentStatus.Assigned]: "Assigned",
  [ShipmentStatus.Delivered]: "Delivered",
  [ShipmentStatus.Incomplete]: "Incomplete",
};

export const ShipmentStatusColorMap = {
  [ShipmentStatus.Draft]: "#D9D9D9",
  [ShipmentStatus.Pending]: "#FFA726",
  [ShipmentStatus.Completed]: "#62DAAB",
  [ShipmentStatus.Posted]: "#2D6AC4",
  [ShipmentStatus.Assigned]: "#52c41a",
  [ShipmentStatus.Delivered]: "#722ed1",
  [ShipmentStatus.Incomplete]: "#F5222D",
};

export const NissanVehicleStatusMap = {
  [NissanVehicleStatus.Available]: "Available",
  [NissanVehicleStatus.Processed]: "Processed",
  [NissanVehicleStatus.ShippedManually]: "Shipped Manually",
  [NissanVehicleStatus.ExceptionDriverRemoved]: "Exception Driver Removed",
  [NissanVehicleStatus.Delivered]: "Delivered",
  [NissanVehicleStatus.Archived]: "Archived",
};

export const NissanVehicleStatusColorMap = {
  [NissanVehicleStatus.Available]: "green",
  [NissanVehicleStatus.Processed]: "blue",
  [NissanVehicleStatus.ShippedManually]: "magenta",
  [NissanVehicleStatus.ExceptionDriverRemoved]: "purple",
  [NissanVehicleStatus.Delivered]: "orange",
  [NissanVehicleStatus.Archived]: "gray",
};

export const LoadStatusMap = {
  [LoadStatus.Active]: "Active",
  [LoadStatus.PickedUp]: "Picked Up",
  [LoadStatus.Delivered]: "Delivered",
  [LoadStatus.Completed]: "Completed",
  [LoadStatus.Incomplete]: "Incomplete",
};

export const WeekDays = {
  MONDAY: "MON",
  TUESDAY: "TUE",
  WEDNESDAY: "WED",
  THURSDAY: "THU",
  FRIDAY: "FRI",
  SATURDAY: "SAT",
  SUNDAY: "SUN",
};

export const WeekDaysMap = {
  [WeekDays.MONDAY]: "MON",
  [WeekDays.TUESDAY]: "TUE",
  [WeekDays.WEDNESDAY]: "WED",
  [WeekDays.THURSDAY]: "THU",
  [WeekDays.FRIDAY]: "FRI",
  [WeekDays.SATURDAY]: "SAT",
  [WeekDays.SUNDAY]: "SUN",
};

export const HardRouteLocationMap = {
  [HardRouteLocation.NormalRoute]: "Neither",
  [HardRouteLocation.DifficultPickup]: "Pickup",
  [HardRouteLocation.DifficultDropoff]: "Dropoff",
  [HardRouteLocation.Both]: "Both",
};

export const ShipmentStatusStep = {
  [ShipmentStatus.Draft]: 0,
  [ShipmentStatus.Pending]: 1,
  [ShipmentStatus.Completed]: 1,
  [ShipmentStatus.Posted]: 2,
  [ShipmentStatus.Assigned]: 3,
  [ShipmentStatus.Delivered]: 4,
  [ShipmentStatus.Incomplete]: 5,
};

export const QuoteStatusMap = {
  [QuoteStatus.None]: "None",
  [QuoteStatus.Pending]: "Pending",
  [QuoteStatus.Completed]: "Completed",
  [QuoteStatus.Expired]: "Expired",
};

export const PricingEngineResultStatus = {
  Success: 1,
  Warning: 2,
  Error: 3,
};

export const PricingEngineResultStatusMap = {
  [PricingEngineResultStatus.Success]: {
    message: "Success",
    type: "success",
  },
  [PricingEngineResultStatus.Warning]: {
    message: "Warning",
    type: "warning",
  },
  [PricingEngineResultStatus.Error]: {
    message: "Error",
    type: "error",
  },
};

export const GatePassStatusMap = {
  [GatePassType.NotRequired]: "Not Required",
  [GatePassType.Pending]: "Pending",
  [GatePassType.Attached]: "Attached",
};

export const GatePassStatusColor = {
  [GatePassType.NotRequired]: "default",
  [GatePassType.Pending]: "processing",
  [GatePassType.Attached]: "success",
};

export const GatePassStatusColorLabel = {
  [GatePassType.NotRequired]: "Not Required",
  [GatePassType.Pending]: "Pending",
  [GatePassType.Attached]: "Attached",
};

export const DrivabilityMap = {
  [Drivability.RideAndDrive]: "Ride and Drive",
  [Drivability.Inoperational]: "Inoperational",
};

export const FuelTypeMap = {
  [FuelType.Gasoline]: "Gasoline",
  [FuelType.Diesel]: "Diesel",
  [FuelType.Hybrid]: "Hybrid",
  [FuelType.Electric]: "Electric",
  [FuelType.Hydrogen]: "Hydrogen",
  [FuelType.Other]: "Other",
};

export const TrailerTypeMap = {
  [TrailerType.Open]: "Open Trailer",
  [TrailerType.Enclosed]: "Enclosed",
};

export const CompanyStatus = {
  Active: 1,
  Duplicate: 2,
  Block: 3,
  MissingInfo: 4,
  SoftBlock: 5,
};

export const CompanyStatusMap = {
  [CompanyStatus.Active]: "Active",
  [CompanyStatus.Duplicate]: "Duplicate",
  [CompanyStatus.Block]: "Block",
  [CompanyStatus.MissingInfo]: "Missing Info",
  [CompanyStatus.SoftBlock]: "Soft Block",
};

export const Integrations = {
  WWL: 1,
};

export const AssociatedUserType = {
  Company: 1,
  Group: 2,
};

export const AssociatedUserTypeMap = {
  [AssociatedUserType.Company]: "Company",
  [AssociatedUserType.Group]: "Group",
  [AssociatedUserType.SharedDealer]: "Shared Dealer",
};

export const ShipmentCancelReasonOptions = [
  { value: ShipmentCancelReason.Price, title: "Price" },
  { value: ShipmentCancelReason.NoLongerNeeded, title: "No Longer Needed" },
  { value: ShipmentCancelReason.DealFellThrough, title: "Deal Fell Through" },
  { value: ShipmentCancelReason.VehicleMechanicalIssues, title: "Vehicle Mechanical Issue" },
  { value: ShipmentCancelReason.Other, title: "Other" },
];

export const DealerExchangeTypeMap = {
  [DealerExchange.Ordered]: "Ordered",
  [DealerExchange.Unordered]: "Unordered",
};

export const EntityType = {
  Dealer: 1,
  Transporter: 2,
  DealerAdmin: 3,
};

export const EntityTypeMap = {
  [EntityType.Dealer]: "Dealer",
  [EntityType.Transporter]: "Transporter",
  [EntityType.DealerAdmin]: "Dealer Admin",
};

export const PriceAdjustType = {
  ToDollarAmount: 1,
  ByDollarAmount: 2,
  ByPercentage: 3,
};

export const PriceAdjustmentDirection = {
  Increase: 1,
  Decrease: 2,
};

export const Orientation = {
  Horizontal: 1,
  Vertical: 2,
};

export const QuoteFeedbackTypeMap = {
  [QuoteFeedbackType.PriceSeemsIncorrect]: "Price Seems Incorrect",
  [QuoteFeedbackType.TemporaryMarketConditions]: "Temporary Market Conditions",
  [QuoteFeedbackType.MultiVehicleShipment]: "Multi-Vehicle Shipment",
  [QuoteFeedbackType.StrategicDiscounting]: "Strategic Discounting",
  [QuoteFeedbackType.CustomContractPricing]: "Custom Contract Pricing",
  [QuoteFeedbackType.Other]: "Other (Please Specify)",
  [QuoteFeedbackType.InvalidRule]: "Invalid Rule",
  [QuoteFeedbackType.NewRoute]: "New Route",
  [QuoteFeedbackType.VehicleException]: "Vehicle Exception",
  [QuoteFeedbackType.ExpeditedTimeline]: "Expedited Timeline",
  [QuoteFeedbackType.HardRoute]: "Hard Route",
  [QuoteFeedbackType.SystemError]: "System Error / Missing Price",
  [QuoteFeedbackType.QuickQuote]: "Quick Quote",
};

export const VehicleColor = {
  Beige: "Beige",
  Black: "Black",
  Blue: "Blue",
  Brown: "Brown",
  Gray: "Gray",
  Green: "Green",
  LightBlue: "LightBlue",
  Orange: "Orange",
  Red: "Red",
  Silver: "Silver",
  White: "White",
  Yellow: "Yellow",
  Other: "Other",
};

export const BillOfLadingMediaPurposeMap = {
  [BillOfLadingMediaPurpose.Roof]: "Roof",
  [BillOfLadingMediaPurpose.DriverSide]: "Driver Side",
  [BillOfLadingMediaPurpose.PassengerSide]: "Passenger Side",
  [BillOfLadingMediaPurpose.CornerFrontPassenger]: "Corner Front Passenger",
  [BillOfLadingMediaPurpose.CornerFrontDriver]: "Corner Front Driver",
  [BillOfLadingMediaPurpose.CornerRearPassenger]: "Corner Rear Passenger",
  [BillOfLadingMediaPurpose.CornerRearDriver]: "Corner Rear Driver",
  [BillOfLadingMediaPurpose.Odometer]: "Odometer",
  [BillOfLadingMediaPurpose.Signatures]: "Signatures",
  [BillOfLadingMediaPurpose.Additional]: "Additional",
  [BillOfLadingMediaPurpose.PowerUnit]: "Power Unit",
};

export const FixedByZipPriceType = {
  Fixed: 1,
  Adjustable: 2,
};

export const FixedByZipPriceTypeMap = {
  [FixedByZipPriceType.Fixed]: "Fixed",
  [FixedByZipPriceType.Adjustable]: "Adjustable",
};

export const FixedByZipDirectionType = {
  OneWay: 1,
  Both: 2,
};

export const FixedByZipDirectionTypeMap = {
  [FixedByZipDirectionType.OneWay]: "One Way",
  [FixedByZipDirectionType.Both]: "Both",
};

export const LoadTimeSlot = {
  EighthAmToTwelvePm: 1,
  TwelvePmToFourPm: 2,
  FourPmToEighthPm: 3,
};

export const LoadTimeSlotMap = {
  [LoadTimeSlot.EighthAmToTwelvePm]: "8 AM to 12 PM",
  [LoadTimeSlot.TwelvePmToFourPm]: "12 PM to 4 PM",
  [LoadTimeSlot.FourPmToEighthPm]: "4 PM to 8 PM",
};

export const AssociateWithUserType = {
  CreateNewUser: 1,
  AssociateWithExistingUser: 2,
};

export const BillStatus = {
  Unpaid: 1,
  Paid: 2,
  Pending: 3,
  LoadIssues: 4,
  ACHIssues: 5,
};

export const BillStatusMap = {
  [BillStatus.Unpaid]: "Unpaid",
  [BillStatus.Paid]: "Paid",
  [BillStatus.Pending]: "Pending",
  [BillStatus.LoadIssues]: "Load Issues",
  [BillStatus.ACHIssues]: "ACH Issues",
};

export const InvoiceStatus = {
  NotReceived: 1,
  Billed: 2,
  Received: 3,
  Pending: 4,
};

export const InvoiceStatusMap = {
  [InvoiceStatus.NotReceived]: "Not Received",
  [InvoiceStatus.Billed]: "Billed",
  [InvoiceStatus.Received]: "Received",
  [InvoiceStatus.Pending]: "Pending",
};

export const BillStatusColorMap = {
  [BillStatus.Unpaid]: { label: "Unpaid", color: "#d4af37" },
  [BillStatus.Paid]: { label: "Paid", color: "#52c41a" },
  [BillStatus.Pending]: { label: "Pending", color: "#f5222d" },
  [BillStatus.LoadIssues]: { label: "Load Issues", color: "purple" },
  [BillStatus.ACHIssues]: { label: "ACH Issues", color: "orange" },
};

export const InvoiceStatusColorMap = {
  [InvoiceStatus.NotReceived]: { label: "Not Received", color: "#f5222d" },
  [InvoiceStatus.Billed]: { label: "Billed", color: "#2D6AC4" },
  [InvoiceStatus.Received]: { label: "Received", color: "#52c41a" },
  [InvoiceStatus.Pending]: { label: "Pending", color: "#d4af37" },
};

export const EMPTY_BOL = {
  PICKUP: "no-pickup",
  DROPOFF: "no-dropoff",
};

export const EMPTY_BOL_MAP = {
  [EMPTY_BOL.PICKUP]: "picked up",
  [EMPTY_BOL.DROPOFF]: "dropped off",
};

export const ShipmentIncompleteInitiatedBy = {
  Autosled: 1,
  Dealer: 2,
  Transporter: 3,
};

export const initiatedByOptions = [
  { value: ShipmentIncompleteInitiatedBy.Autosled, title: "Autosled" },
  { value: ShipmentIncompleteInitiatedBy.Dealer, title: "Dealer" },
  {
    value: ShipmentIncompleteInitiatedBy.Transporter,
    title: "Transporter",
  },
];

export const AutosledReasonMap = {
  [AutosledCancelReason.NoResponseFromTransporter]: "No Response from Transporter",
  [AutosledCancelReason.ExchangeWasCanceled]: "Exchange Was Canceled",
  [AutosledCancelReason.TooManyETAChanges]: "Too Many ETA Changes",
  [AutosledCancelReason.ChangeOfRoute]: "Change of Route",
  [AutosledCancelReason.EquipmentMalfunction]: "Equipment Malfunction",
  [AutosledCancelReason.TookTooLong]: "Took Too Long",
  [AutosledCancelReason.Other]: "Other",
};

export const ShipperReasonMap = {
  [ShipperCancelReason.Price]: "Price",
  [ShipperCancelReason.NoLongerNeeded]: "No Longer Needed",
  [ShipperCancelReason.DealFellThrough]: "Deal Fell Through",
  [ShipperCancelReason.VehicleMechanicalIssues]: "Vehicle Mechanical Issues",
  [ShipperCancelReason.TookTooLong]: "Took Too Long",
  [ShipperCancelReason.Other]: "Other",
};

export const DispatchSourceMap = {
  [DispatchSource.AutosledDashboard]: "Autosled Dashboard",
  [DispatchSource.Email]: "Email",
  [DispatchSource.CentralDispatch]: "Central Dispatch",
  [DispatchSource.SuperDispatch]: "Super Dispatch",
  [DispatchSource.Other]: "Other",
};

export const TransporterReasonMap = {
  [TransporterCancelReason.EquipmentMalfunction]: {
    title: "Equipment Malfunction",
    icon: <TruckOutlined />,
  },
  [TransporterCancelReason.ChangeOfRoute]: {
    title: "Change of Route",
    icon: <SwapOutlined />,
  },
  [TransporterCancelReason.UnitNotReady]: {
    title: "Unit Not Ready",
    icon: <DashboardOutlined />,
  },
  [TransporterCancelReason.UnitNotAsDescribed]: {
    title: "Unit Not As Described",
    icon: <RocketOutlined />,
  },
  [TransporterCancelReason.Reconsidered]: {
    title: "Reconsidered",
    icon: <RetweetOutlined />,
  },
  [TransporterCancelReason.RenegotiatingRate]: {
    title: "Renegotiating Rate",
    icon: <DollarOutlined />,
  },
  [TransporterCancelReason.NoGatepass]: {
    title: "No Gatepass",
    icon: <FileExcelOutlined />,
  },
  [TransporterCancelReason.NoPSI]: {
    title: "No PSI",
    icon: <ToolOutlined />,
  },
  [TransporterCancelReason.UnitDoesntFit]: {
    title: "Unit Doesn't Fit",
    icon: <FullscreenOutlined />,
  },
  [TransporterCancelReason.CantMakeETA]: {
    title: "Can't Make ETA",
    icon: <ClockCircleOutlined />,
  },
  [TransporterCancelReason.DriverSwitch]: {
    title: "Driver Switch",
    icon: <UserSwitchOutlined />,
  },
  [TransporterCancelReason.PickupLocationIssue]: {
    title: "Pickup Location Issue",
    icon: <EnvironmentOutlined />,
  },
  [TransporterCancelReason.Other]: {
    title: "Other",
    icon: <CloseCircleOutlined />,
  },
};

export const reasons = {
  [ShipmentIncompleteInitiatedBy.Autosled]: Object.values(AutosledCancelReason)
    .filter(reason => typeof reason === "number")
    .map(reason => ({
      value: reason,
      title: AutosledReasonMap[reason],
    })),
  [ShipmentIncompleteInitiatedBy.Dealer]: Object.values(ShipperCancelReason)
    .filter(reason => typeof reason === "number")
    .map(reason => ({
      value: reason,
      title: ShipperReasonMap[reason],
    })),
  [ShipmentIncompleteInitiatedBy.Transporter]: Object.values(TransporterCancelReason)
    .filter(reason => typeof reason === "number")
    .map(reason => ({
      value: reason,
      title: TransporterReasonMap[reason].title,
    })),
};

export const STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
};

export const VehicleSize = {
  SMALL: 1,
  MEDIUM: 2,
  LARGE: 3,
  XL: 4,
  UNKNOWN: 5,
};

export const SecurePassAutoSend = {
  NOT_SEND: 1,
  DEFAULT: 2,
  SEND: 3,
};

export const SecurePassAutoSendMap = {
  [SecurePassAutoSend.NOT_SEND]: "Do Not Send",
  [SecurePassAutoSend.DEFAULT]: "Use for High Value Vehicles",
  [SecurePassAutoSend.SEND]: "Send",
};

export const LocationType = {
  Pickup: "Pickup",
  Dropoff: "Dropoff",
};

export const SECURE_PASS_TAG_STATUS_MAP = {
  [SecurePassTagStatus.Disabled]: "Disabled",
  [SecurePassTagStatus.MissingContact]: "Needs Info",
  [SecurePassTagStatus.Enabled]: "Enabled",
  [SecurePassTagStatus.FailedOutreach]: "Failed Outreach",
  [SecurePassTagStatus.FailedValidation]: "Failed Validation",
  [SecurePassTagStatus.Overridden]: "Override",
};

export const SecurePassLogTypeMap = {
  [SecurePassLogType.QRScanned]: "QR Scanned",
  [SecurePassLogType.VerificationAttempt]: "Verification Attempt",
  [SecurePassLogType.OutreachAttempt]: "Outreach Attempt",
  [SecurePassLogType.QRCodeRetrieved]: "QR Code Retrieved",
  [SecurePassLogType.SecurePassSend]: "Secure Pass Send",
  [SecurePassLogType.VerificationOverride]: "Verification Override",
  [SecurePassLogType.SecurePassCreateAttempt]: "Secure Pass Create Attempt",
};

export const SecurePassLogTypeIcon = {
  [SecurePassLogType.QRScanned]: <MobileFilled />,
  [SecurePassLogType.QRCodeRetrieved]: <QrcodeOutlined />,
  [SecurePassLogType.SecurePassSend]: <MailFilled />,
  [SecurePassLogType.OutreachAttempt]: <PhoneFilled />,
  [SecurePassLogType.VerificationAttempt]: <KeyOutlined />,
  [SecurePassLogType.VerificationOverride]: <KeyOutlined />,
  [SecurePassLogType.SecurePassCreateAttempt]: <MailFilled />,
};

export const ShipmentLockedMap = {
  [ShipmentLocked.OnHold]: "On Hold",
  [ShipmentLocked.Pending]: "Pending",
};

export const SalesforceAccountStatusMap = {
  [SalesforceAccountStatus.Prospect]: "Prospect",
  [SalesforceAccountStatus.SignedUp]: "Signed Up",
  [SalesforceAccountStatus.Active]: "Active",
  [SalesforceAccountStatus.Churned]: "Churned",
};

export const UserCreationType = {
  CREATE_NEW_USER: 1,
  SELECT_EXISTING_USER: 2,
};

export const UserCreationTypeMap = {
  [UserCreationType.CREATE_NEW_USER]: "Create New User",
  [UserCreationType.SELECT_EXISTING_USER]: "Use Existing Shared User",
};

export const RUCARiskScoreMap = {
  [RUCARiskScore.LowRisk]: {
    label: "Low Risk",
    color: "green",
  },
  [RUCARiskScore.MediumRisk]: {
    label: "Medium Risk",
    color: "yellow",
  },
  [RUCARiskScore.HighRisk]: {
    label: "High Risk",
    color: "orange",
  },
  [RUCARiskScore.VeryHighRisk]: {
    label: "Very High Risk",
    color: "red",
  },
};
